import Store from '@ember-data/store';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import LineItemModel from 'donor-elf-web/models/line-item';
import AbilitiesService from 'donor-elf-web/services/abilities';
import FundsService from 'donor-elf-web/services/funds-service';
import NavbarSettingsService from 'donor-elf-web/services/navbar-settings';
import Settings from 'donor-elf-web/services/settings';
import TransactionsService, {
  type LineItemInfo,
} from 'donor-elf-web/services/transactions-service';
import RSVP from 'rsvp';

export class TransactionsEditEnterRouteModel {
  constructor(
    focusOn: TransactionsEditEnterFocusOn,
    public lastGiftForContact: { [contactId: string]: LineItemModel },
    public transTypes: string[],
  ) {
    this.focusOn = focusOn;
  }

  @tracked focusOn: TransactionsEditEnterFocusOn;
}

export interface TransactionsEditEnterFocusOn {
  changeset?: any;
  fieldName?: string;
}

export default class TransactionsEditEnterRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare fundsService: FundsService;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare settings: Settings;
  @service declare store: Store;
  @service declare storeCache: any;
  @service declare transactionsService: TransactionsService;

  queryParams = {
    contactId: {},
  };

  async model(
    params: any,
    transition: any,
  ): Promise<TransactionsEditEnterRouteModel> {
    if (params.id === 'add') {
      this.navbarSettings.title = 'Add Transactions';
    } else {
      this.navbarSettings.title = 'Edit Transactions';
    }

    let lineItemInfos = this.transactionsService.lineItemInfos;
    let retrieveLineItemPromise = Promise.resolve();

    if (params.id !== 'add') {
      retrieveLineItemPromise = this.store
        .findRecord('line-item', params.id)
        .then((model) => {
          let record = this.transactionsService.createInfoFromModel(model);
          lineItemInfos.push(record);
        });
    }

    await retrieveLineItemPromise;
    if (lineItemInfos.length === 0) {
      let model = this.store.createRecord('line-item', {
        transDate: new Date(),
      });
      if (isPresent(params.contactId)) {
        model.contactId = params.contactId;
      }
      lineItemInfos.push(this.transactionsService.createInfoFromModel(model));
    }
    let focusOn = {};
    if (params.id === 'add') {
      focusOn = {
        changeset: lineItemInfos[lineItemInfos.length - 1].changeset,
        fieldName: 'transDate',
      };
    } else {
      let existingRecord = lineItemInfos[0];
      // Can't edit non-manual transaction if you don't have the edit_all_transactions ability
      if (
        !existingRecord.model.isManual &&
        this.abilities.noAbility('edit_all_transactions')
      ) {
        transition.abort();
        return new TransactionsEditEnterRouteModel(focusOn, {}, []);
      }
    }
    this.transactionsService.lineItemInfos = lineItemInfos;
    return RSVP.hash({
      focusOn,
      lastGiftForContact: this.getLastGiftForContact(),
      transTypes: this.getTransTypes(lineItemInfos),
    }).then((results) => {
      return new TransactionsEditEnterRouteModel(
        results.focusOn,
        results.lastGiftForContact,
        results.transTypes,
      );
    });
  }

  getLastGiftForContact(): Promise<{ [contactId: string]: LineItemModel }> {
    return this.store
      .query('line-item', { filter: { last_gift: true } })
      .then((lastGifts) => {
        let lastGiftForContact: any = {};
        for (let lineItem of lastGifts.slice()) {
          lastGiftForContact[lineItem.contactId!] = lineItem;
        }
        return lastGiftForContact;
      });
  }

  getTransTypes(lineItemInfos: LineItemInfo[]) {
    if (
      lineItemInfos.length === 1 &&
      lineItemInfos[0].model.isNew &&
      this.settings.orgSettings?.manualTransactionTypes != null
    ) {
      return this.settings.orgSettings.manualTransactionTypes;
    }
    return this.storeCache
      .findRecord(
        'transaction-filter-option',
        this.fundsService.selectedFund!.id,
      )
      .then((record: any) => {
        let transTypes = record.transTypes;
        if (transTypes.length === 0) {
          transTypes.push('Gift');
        }
        return transTypes;
      });
  }
}
